/**
 * This JS file is common to the product card sections "product-mattress"
 * and "product-accessory-v2".
 * NOTE: Most components in this are named as if they are meant only for mattress
 * pages. This is because they were originally built for only mattresses but is
 * being expanded to work with accessories. However, as we are working concurrently
 * with a developer on the comparison page, it would be best for me to rename
 * most of the components after merging with him.
 *
 * It contains a global state object and several custom elements
 * used by the product card at the top of the mattress pages. Each custom element
 * is responsible for interpreting the global state object to render their dynamic
 * content. The custom elements can subscribe to events that notify when key values
 * in the state change, such as the options selected.
 *
 * Dependencies:
 *    - main.js (main JS file that runs on all pages)
 *    - OptOutDiscountDict (dictionary of opt out discount values)
 */

/**
 * Initialize global product JSON
 */
try {
  window.NolahProductJSON = JSON.parse(
    document.querySelector("[data-product-json]").innerHTML
  );
} catch (err) {
  console.error("Could not check product JSON", err);
}

/**
 * This is a state object in the global scope for product pages that custom
 * elements refer to. This object is designed to hold simply the options selected
 * and the addons (upsells) selected, while offering accessors that derive
 * every other complex value that we need.
 */
window.NolahPDPState = {
  /**
   * Standard shopify product json.
   */
  productJSON: window.NolahProductJSON.product,
  defaultFreeGift: window.NolahProductJSON.selected_gift,
  numFreeGifts: window.NolahProductJSON.num_free_gifts || 0,
  isMattressProd: window.NolahProductJSON.product.tags.includes("mattress"),
  isWeightedBlanket:
    window.NolahProductJSON.product.id === window.weightedBlanketProdID,
  isNatural11Prod: window.NolahProductJSON.product.id === 6578691702863,
  hasSale50Tag: window.NolahProductJSON.product.tags.includes("sale50"),

  /**
   * The state of what options are selected. Should only be edited using functions
   * like setOptionByName.
   */
  optionsSelected: {
    option1: window.NolahProductJSON.selected_variant.option1,
    option2: window.NolahProductJSON.selected_variant.option2,
    option3: window.NolahProductJSON.selected_variant.option3,
  },

  // The number of the main product to add when clicking the ATC button.
  mainProdQtyToAdd: 1,

  /**
   * Mattress-only state property.
   * Some mattresses have a 'fake' split king option value. When such a split king
   * option is selected, the true option selected in this state object is
   * 'Twin XL', and this flag will turn true. Then, we can check for this flag
   * to know to add two 'Twin XL' mattresses instead of one.
   * It can be assumed that if this is true, this is a mattress product, so
   * you don't need to check for isMattressProd too.
   */
  pseudoSplitKingSelected: false,

  /**
   * Mattress-only state property.
   * If a pseudo split king option is selected, then we can store the firmness of
   * the second mattress in this variable.
   */
  secondFirmness: null,

  /**
   * Upsells and addons selected, not including free gifts. List of variants.
   */
  addonsSelected: [],

  /**
   * Free gift selected. A product object with variants inside. If falsy
   * then there are no free gifts selected.
   */
  giftSelected: window.NolahProductJSON.selected_gift,

  /**
   * Free gift variant id selected. Optional and only if a specific variant
   * is selected (this was added for weighted blankets).
   * If this isn't null then adding to cart will override usual logic that
   * determines which variant is chosen, such as the logic that chooses the size
   * that matches the mattress.
   */
  giftVariantSelected: null,

  /**
   * You can use this to change the selected product that is referenced and
   * added to cart. Be wary that this does not reselect new options.
   * @param productJSON a standard shopify product json
   */
  setProductJSON(productJSON) {
    this.productJSON = productJSON;

    // Though it is not technically options that changed, but the whole product
    // we emit the option-changed event.
    document.dispatchEvent(
      new CustomEvent("state:option-changed", {
        bubbles: true,
        detail: {
          productJSONChanged: true,
        },
      })
    );
  },

  /**
   * Returns the current selected variant by checking optionsSelected, which
   * is updated with every option change.
   */
  get currentVariant() {
    const selectedValues = Object.keys(this.optionsSelected).map((key) => {
      return {
        optionKey: key,
        value: this.optionsSelected[key],
      };
    });
    const variants = this.productJSON.variants;
    const found = variants.find(function (variant) {
      return selectedValues.every(function (values) {
        return variant[values.optionKey] === values.value;
      });
    });

    return found;
  },

  /**
   * @param number newQty
   * @param boolean silent
   */
  setQtyToAdd(newQty, silent = false) {
    if (typeof newQty !== "number" || Number.isNaN(newQty)) {
      throw "Given quantity is NaN";
    }

    if (newQty !== this.mainProdQtyToAdd) {
      this.mainProdQtyToAdd = newQty;

      if (!silent) {
        document.dispatchEvent(
          new CustomEvent("state:qty-input-changed", {
            bubbles: true,
          })
        );
      }
    }
  },

  /**
   * Get the current variant price, taking into account that we check if
   * the compare at price is lower than the price, which means its targeted
   * to be discounted by a plus script.
   *
   * If a pseudo split king size is selected, the selected variant is the Twin XL,
   * and the price is 2x the Twin XL's.
   *
   * Does not take the selected quantity selector's value into account.
   *
   * @param [variant] Optional param helpful to avoid computing this.currentVariant again
   */
  getPrice(variant) {
    let v = variant || this.currentVariant;

    let lowerPrice = Math.min(v.price, v.compare_at_price);
    if (this.pseudoSplitKingSelected) {
      lowerPrice = lowerPrice * 2;
    }

    return lowerPrice;
  },

  /**
   * Same as getPrice but takes selected quantity into account.
   * @param [variant] Optional param helpful to avoid computing this.currentVariant again
   */
  getLinePrice(variant) {
    return this.getPrice(variant) * this.mainProdQtyToAdd;
  },

  /**
   * Same as currentPrice but with the compare at price.
   *
   * @param [variant] Optional param helpful to avoid computing this.currentVariant again
   */
  getComparePrice(variant) {
    let v = variant || this.currentVariant;

    let higherPrice = Math.max(v.price, v.compare_at_price);
    if (this.pseudoSplitKingSelected) {
      higherPrice = higherPrice * 2;
    }

    return higherPrice;
  },

  /**
   * Same as getPrice but takes selected quantity into account.
   * @param [variant] Optional param helpful to avoid computing this.currentVariant again
   */
  getLineComparePrice(variant) {
    return this.getComparePrice(variant) * this.mainProdQtyToAdd;
  },

  /**
   * Get both price and compare at price in a tuple.
   */
  getPriceTuple() {
    const variant = this.currentVariant;
    const price = this.getPrice(variant);
    const comparePrice = this.getComparePrice(variant);
    return [price, comparePrice];
  },

  /**
   * Get the total price including with addons. Be wary that we consider
   * whether the compare at price is lower than the price, which means it is
   * targeted to be discounted by a plus script.
   *
   * Some addons are discounts, and their existence means we have to subtract
   * from the total (opt out discounts).
   *
   * If a pseudo split king size is selected, we have to account for its price
   * of 2x the selected Twin XL.
   */
  get totalPrice() {
    const variant = this.currentVariant;
    const variantPrice = this.getLinePrice(variant);
    const totalAddonPrice = this.totalAddonPrice;

    // Get any mulberry prices
    const warrantyCostInCents = this.selectedMulberryCost;

    return variantPrice + totalAddonPrice + warrantyCostInCents;
  },

  /**
   * Get the total price including with addons. Be wary that we consider
   * whether the compare at price is lower than the price, which means it is
   * targeted to be discounted by a plus script.
   *
   * Like with the normal price we calculate the pseudo split king to be 2x
   * the selected Twin XL.
   */
  get totalComparePrice() {
    const variant = this.currentVariant;
    const variantComparePrice = this.getLineComparePrice(variant);
    const totalAddonComparePrice = this.totalAddonComparePrice;
    const warrantyCostInCents = this.selectedMulberryCost;

    return variantComparePrice + totalAddonComparePrice + warrantyCostInCents;
  },

  get numItemsToBeAdded() {
    const numAddons = this.numAddonsSelected;

    // Be sure to count mulberry addons
    let numMulberryAddons = 0;
    if (window.MulberryPDPState && window.MulberryPDPState.selectedOffer) {
      numMulberryAddons = this.pseudoSplitKingSelected ? 2 : 1;
    }

    let totalNumFreeGifts = 0;
    const giftInfo = this.getMatchingGiftVariantInfo();
    if (giftInfo) {
      totalNumFreeGifts = giftInfo.quantity;
    }

    const numMattresses =
      (this.pseudoSplitKingSelected ? 2 : 1) * this.mainProdQtyToAdd;
if (window.freeGiftEnabled === true) {
    return numMattresses + totalNumFreeGifts + numAddons + numMulberryAddons;
}else{
   return numMattresses + numAddons + numMulberryAddons;
}
  },

  /**
   * Does not include mulberry, but just standard upsells and opt outs.
   */
  get numAddonsSelected() {
    return this.addonsSelected.length;
  },

  get totalAddonPrice() {
    let total = this.addonsSelected.reduce((prev, curr) => {
      if (curr.compare_at_price < curr.price) {
        return prev + curr.compare_at_price;
      } else {
        return prev + curr.price;
      }
    }, 0);

    // Get any discount-style opt out addons
    const optOutAddon = this.addonsSelected.find((a) =>
      a.name.includes("Opt-Out")
    );
    if (optOutAddon) {
      total = total - this.optOutDiscountAmt;
    }

    return total;
  },

  get totalAddonComparePrice() {
    let total = this.addonsSelected.reduce((prev, curr) => {
      if (curr.compare_at_price < curr.price) {
        return prev + curr.price;
      } else {
        return prev + curr.compare_at_price;
      }
    }, 0);

    return total;
  },

  get selectedMulberryCost() {
    let warrantyCostInCents = 0;
    if (window.MulberryPDPState && window.MulberryPDPState.selectedOffer) {
      const costString = window.MulberryPDPState.selectedOffer.cost;
      warrantyCostInCents = Math.round(parseFloat(costString) * 100);

      // Account for pseudo split king (2x Twin XL).
      if (this.pseudoSplitKingSelected) {
        warrantyCostInCents = warrantyCostInCents * 2;
      }

      if (Number.isNaN(warrantyCostInCents)) {
        console.error(
          "NaN: Mulberry warranty pricing values may need to be checked."
        );
        warrantyCostInCents = 0;
      }
    }

    return warrantyCostInCents;
  },

  /**
   * Get the current opt out discount amount for the variant selected now.
   * Accounts for the pseudo split king variant.
   */
  get optOutDiscountAmt() {
    const discountInfo = window.OptOutDiscountDict[this.productJSON.id];
    if (discountInfo) {
      let discountAmt = discountInfo[this.currentVariant.title] || 0;
      if (this.pseudoSplitKingSelected) {
        return discountAmt * 2;
      } else {
        return discountAmt;
      }
    } else {
      return 0;
    }
  },

  get isOptOutAdded() {
    const optOutAddon = this.addonsSelected.find((a) =>
      a.name.includes("Opt-Out")
    );
    return typeof optOutAddon !== "undefined";
  },

  isOfferingFreeGift() {
    return this.giftSelected;
  },

  /**
   * Returns an info object with the variant of the selected gift that matches
   * the main size option selected.
   */
  /* getMatchingGiftVariantInfo() {
    // FIXME Make this entire block more DRY
    if (this.giftSelected) {
      const giftInfo = { variant: null, quantity: 1 };

      // If a specific variant ID is selected, return the variant selected and
      // how many should be added. Weighted blankets fall under this conditional.
      if (this.giftVariantSelected) {
        const foundVariant = this.giftSelected.variants.find(
          (v) => v.id === this.giftVariantSelected
        );
        giftInfo.variant = foundVariant;

        // Offer 2 variants if split king is selected.
        if (this.pseudoSplitKingSelected) {
          giftInfo.quantity = 2;
        }
        // console.log("1. Before selecting variant: " + JSON.stringify(giftInfo));

        return giftInfo;
      }

      // When squishy pillows are the gift, we can just offer the first variant.
      if (this.giftSelected.id === window.giftPillowProdID) {
        giftInfo.variant = this.giftSelected.variants[0]; // Queen / 2-Pack

        // Offer 2 pillow variants if split king is selected.
        if (this.pseudoSplitKingSelected) {
          giftInfo.quantity = 2;
        }
        // console.log("2. Before selecting variant: " + JSON.stringify(giftInfo));

        return giftInfo;
      }

      // Otherwise try to find a size that matches the currently selected
      // mattress size in the gift product's variants
      const currentSize = this.getOptionByName("Size");
      if (currentSize) {
        let foundVariant = this.giftSelected.variants.find((v) => {
          return v.options.some((o) => o.includes(currentSize));
        });

        // If the currentSize is Split King but there's no matching variant,
        // add two Twin XLs.
        if (this.pseudoSplitKingSelected && !foundVariant) {
          foundVariant = this.giftSelected.variants.find((v) => {
            return v.options.some((o) => o.includes("Twin XL"));
          });

          giftInfo.quantity = 2;
        }

        giftInfo.variant = foundVariant;
        console.log("After selecting variant: " + JSON.stringify(giftInfo));
        // console.log("foundVariant " + foundVariant);

        return giftInfo;
      }
    }

    return null;
  } */

  getMatchingGiftVariantInfo() {
    if (this.giftSelected) {
      const giftInfo = { variant: null, quantity: 1 };

      // If a specific variant ID is selected, return the variant selected and
      // how many should be added. Weighted blankets fall under this conditional.
      if (this.giftVariantSelected) {
        const foundVariant = this.giftSelected.variants.find(
          (v) => v.id === this.giftVariantSelected
        );
        giftInfo.variant = foundVariant;

        // Offer 2 variants if split king is selected.
        if (this.pseudoSplitKingSelected) {
          giftInfo.quantity = 2;
        }

        console.log("Before selecting variant: " + JSON.stringify(giftInfo));
        return giftInfo;
      }

      // When squishy pillows are the gift, we can just offer the first variant.
      if (this.giftSelected.id === window.giftPillowProdID) {
        giftInfo.variant = this.giftSelected.variants[0]; // Queen / 2-Pack

        // Offer 2 pillow variants if split king is selected.
        if (this.pseudoSplitKingSelected) {
          giftInfo.quantity = 2;
        }

        console.log("Before selecting variant: " + JSON.stringify(giftInfo));
        return giftInfo;
      }

      // Otherwise try to find a size that matches the currently selected
      // mattress size in the gift product's variants
      const currentSize = this.getOptionByName("Size");
      if (currentSize) {
        let foundVariant = this.giftSelected.variants.find((v) => {
          return v.options.some((o) => {
            if (
              ["Twin", "Twin XL", "Full", "Short Queen"].includes(currentSize)
            ) {
              return o.includes("Queen");
            } else if (
              ["Short King", "Cal King", "Split King"].includes(currentSize)
            ) {
              return o.includes("King");
            } else {
              return o.includes(currentSize);
            }
          });
        });

        // If the currentSize is Split King but there's no matching variant,
        // add two Twin XLs.
        if (this.pseudoSplitKingSelected && !foundVariant) {
          foundVariant = this.giftSelected.variants.find((v) => {
            return v.options.some((o) => o.includes("Twin XL"));
          });

          giftInfo.quantity = 2;
        }

        giftInfo.variant = foundVariant;
        // alert("After selecting variant: " + JSON.stringify(giftInfo));
        return giftInfo;
      }
    }

    return null;
  },

  /**
   * Returns the full price if it weren't free. Returns 0 if there's no gift.
   */
  getMatchingGiftValue() {
    const giftInfo = this.getMatchingGiftVariantInfo();
    let freeGiftValue = 0;
    if (giftInfo && giftInfo.variant) {
      freeGiftValue = giftInfo.variant.price;
      freeGiftValue = freeGiftValue * giftInfo.quantity;
    }
    return freeGiftValue;
  },

  /**
   * Returns the option value given the option name.
   * If the pseudo split king is selected, it will return 'Split King' despite
   * the true current variant being 'Twin XL'.
   *
   * @param name The option name
   */
  getOptionByName(name) {
    if (name === "Size" && this.pseudoSplitKingSelected) {
      return "Split King";
    }

    return this.getTrueOptionByName(name);
  },

  /**
   * Returns the option value given the option name.
   * This always returns the option of the current variant without taking split
   * king into account, like the above getOptionByName does.
   * @param name The option name
   */
  getTrueOptionByName(name) {
    const key = this.getOptionKey(name);
    if (!key) {
      return null;
    }

    return this.optionsSelected[key];
  },

  /**
   * Sets the size option value without resetting the firmness
   */
  setSizeAndResetFirmness(newSize, optionName) {
    if (this.hasFirmnessOption()) {
      // Set size silently first...
      const currentFirmness = this.getOptionByName("Firmness");
      this.setOptionByName("Size", newSize, true /* silent */);

      if (this.pseudoSplitKingSelected) {
        this.secondFirmness = currentFirmness || "Luxury Firm";
      } else {
        this.secondFirmness = null;
      }
      this.setOptionByName("Firmness", currentFirmness || "Luxury Firm");
    } else {
      this.setOptionByName(optionName, newSize);
    }
  },

  /**
   * If applicable, returns two firmnesses, one for each side of the Split King
   * mattresses. (The split king size is actually two Twin XL variants.)
   *
   * Returns a tuple for each firmness: [firmnessOne, firmnessTwo].
   */
  getSplitFirmnesses() {
    const selectedFirmness = this.getOptionByName("Firmness");
    if (selectedFirmness) {
      return [selectedFirmness, this.secondFirmness];
    }
    return [null, null];
  },

  /**
   * If applicable, finds and sets the correct split king style firmness options.
   * @param firmnesses A tuple of firmnesses [side1, side2]
   */
  setSplitFirmnesses(firmnesses) {
    if (this.pseudoSplitKingSelected) {
      this.secondFirmness = firmnesses[1];
    } else {
      this.secondFirmness = null;
    }

    this.setOptionByName("Firmness", firmnesses[0]);
  },

  /**
   * Used for when the pseudo split king option is selected.
   * Gets the second mattress variant, based on the value of this.secondFirmness
   * if applicable. In all other cases, its the same variant as this.currentVariant.
   */
  getSecondSplitMattressVariant() {
    let found;
    const screenWidth = window.innerWidth;

    if (screenWidth <= 767) {
        if (this.hasFirmnessOption()) {
            found = this.productJSON.variants.find((v) => {
                const firmnessKey = this.getOptionKey("Firmness");
                const sizeKey = this.getOptionKey("Size");

                if (this.pseudoSplitKingSelected) {
                    const cover = this.getOptionKey("Cover");
                    const getSelectedCover = document.querySelector('#ProductSelect-Cover-Hidden-Mobile').value;
                    return (
                        v[sizeKey] === "Twin XL" && v[firmnessKey] === this.secondFirmness && v[cover] === getSelectedCover
                    );
                } else {
                    return (
                        v[sizeKey] === "Twin XL" && v[firmnessKey] === this.secondFirmness
                    );
                }
            });
        } else {
            found = this.currentVariant;
        }
    } else {
        if (this.hasFirmnessOption()) {
            found = this.productJSON.variants.find((v) => {
                const firmnessKey = this.getOptionKey("Firmness");
                const sizeKey = this.getOptionKey("Size");

                if (this.pseudoSplitKingSelected) {
                    const cover = this.getOptionKey("Cover");
                    const getSelectedCover = document.querySelector('#ProductSelect-Cover-Hidden').value;
                    return (
                        v[sizeKey] === "Twin XL" && v[firmnessKey] === this.secondFirmness && v[cover] === getSelectedCover
                    );
                } else {
                    return (
                        v[sizeKey] === "Twin XL" && v[firmnessKey] === this.secondFirmness
                    );
                }
            });
        } else {
            found = this.currentVariant;
        }
    }

    return found;
},

  /**
   * Utility function that helps update optionsSelected with the right value.
   * The pseudo split king option selects the Twin XL but also sets a flag
   * indicating that the split king was selected and any form should add
   * two of the selected Twin XLs.
   */
  setOptionByName(name, value, silent = false) {
    console.debug("NolahPDPState: setOptionByName", name, value);
    const key = this.getOptionKey(name);
    if (!key) {
      return null;
    }

    /**
     * Be careful to note when a pseudo split king is selected on a mattress.
     */
    if (this.isMattressProd && name === "Size") {
      if (value === "Split King") {
        this.optionsSelected[key] = "Twin XL";
        this.pseudoSplitKingSelected = true;
      } else {
        this.optionsSelected[key] = value;
        this.pseudoSplitKingSelected = false;
      }
    } else {
      this.optionsSelected[key] = value;
    }

    if (!silent) {
      document.dispatchEvent(
        new CustomEvent("state:option-changed", {
          bubbles: true,
          detail: {
            name,
            value,
          },
        })
      );
    }
  },

  setFreeGift(productObj, variantID) {
    this.giftSelected = productObj;

    // The variant ID is optional and if chosen will be added to cart with the
    // main product. Without a variant ID, we will automatically try to find a
    // good variant that matches the mattress for the user.
    if (variantID) {
      this.giftVariantSelected = variantID;
    } else {
      this.giftVariantSelected = null;
    }

    this._dispatchAddonsChangedEvent();
  },

  /**
   * Get the option key, given an option name. For example,
   * getOptionKey('Size') returns 'option1'.
   */
  getOptionKey(name) {
    const optionIndex = this.productJSON.options.indexOf(name);
    if (optionIndex <= -1) {
      return null;
    }

    return `option${optionIndex + 1}`;
  },

  hasFirmnessOption() {
    return this.productJSON.options.indexOf("Firmness") > -1;
  },

  /**
   * Parameter addon is a variant object that has at a minimum the id and pricing
   * info.
   */
  toggleAddon(addon) {
    if (typeof (addon && addon.id) === "undefined") {
      throw new Error("Addon needs a variant ID.");
    }

    const foundIndex = this.addonsSelected.findIndex((a) => {
      return a.id === addon.id;
    });

    if (foundIndex > -1) {
      this.addonsSelected.splice(foundIndex, 1);
    } else {
      this.addonsSelected.push(addon);
    }

    this._dispatchAddonsChangedEvent({ variant: addon });
  },

  removeAddonsWithNameContaining(substring) {
    this.addonsSelected = this.addonsSelected.filter(
      (a) => !a.name.includes(substring)
    );
    this._dispatchAddonsChangedEvent();
  },

  /**
   * Clears the addons held in the state variable addonsSelected.
   * Note that this does not emit an event.
   */
  clearAddons() {
    this.addonsSelected.length = 0;
  },

  /**
   * This includes the opt out trial in this.addonsSelected. It's a bit of a
   * misnomer because it doesn't add it immediately to cart but adds it with
   * the main mattress product.
   */
  addOptOutTrial() {
    const sizeOptionKey = this.getOptionKey("Size");
    const firmnessOptionKey = this.getOptionKey("Firmness");

    const title = this.productJSON.title;
    const size = this.currentVariant[sizeOptionKey];
    const firmness = this.currentVariant[firmnessOptionKey];

    const optOutVariant = OptOutDiscountIDManager.getVariantWithMattressDetails(
      title,
      size,
      firmness
    );
    if (optOutVariant) {
      this.addonsSelected.push(optOutVariant);
    }

    // Add the second opt out trial if applicable (split king = 2 Twin XLs)
    let secondOptOutVariant;
    if (this.pseudoSplitKingSelected) {
      secondOptOutVariant =
        OptOutDiscountIDManager.getVariantWithMattressDetails(
          title,
          size,
          this.secondFirmness
        );
      if (secondOptOutVariant) {
        this.addonsSelected.push(secondOptOutVariant);
      }
    }

    const addedOptOuts =
      optOutVariant && (!this.pseudoSplitKingSelected || secondOptOutVariant);
    if (addedOptOuts) {
      this._dispatchAddonsChangedEvent();
    } else {
      console.error(
        "Double check opt out variant dictionary.",
        title,
        size,
        firmness,
        this.secondFirmness
      );
    }
  },

  _dispatchAddonsChangedEvent(detail = {}) {
    document.dispatchEvent(
      new CustomEvent("state:addons-changed", {
        bubbles: true,
        detail,
      })
    );
  },
};

window.PDPPageErrorMsg = {
  /**
   * Display a light red error message above the main size select area.
   */
  display(
    message = "Sorry, it looks like you may be having an issue on our site.  Please start a chat or call us at (866) 403-6615."
  ) {
    const errorMessageEl = document.querySelector(
      ".MattressProductForm__ErrorMsg"
    );
    const anchorEl = document.querySelector("#mpf-size-select");
    if (anchorEl) {
      if (errorMessageEl) {
        errorMessageEl.innerHTML = message;
      } else {
        const messageEl = document.createElement("div");
        messageEl.classList.add("MattressProductForm__ErrorMsg");
        messageEl.innerHTML = message;
        anchorEl.parentNode.insertBefore(messageEl, anchorEl);
      }
    }
  },
};

/**
 * Generic product size select.
 * CSS class names refer to mattresses because they were originally built on those
 * pages and are in the process of being updated.
 */
// window.ProductSizeSelectDebugObj helps label which size select is which in logs.
window.ProductSizeSelectDebugObj = { iCount: 0 };
class ProductSizeSelect extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;

    // Debug info
    window.ProductSizeSelectDebugObj.iCount++;
    this.instanceIndex = window.ProductSizeSelectDebugObj.iCount;
    if (this.instanceIndex === 2) {
      console.warn("product-size-select: constructor called more than once");
    }
  }

  connectedCallback() {
    if (this.isConnected) {
      this.cacheElements();
    }

    if (this.isConnected && !this.initialized) {
      this.initialized = true;
      this.syncToCurrentVariant();
      this.init();
    }
  }

  disconnectedCallback() {
    if (!this.isConnected && this.initialized) {
      this.initialized = false;
      this.detachListeners();
    }
  }

  init() {
    this.attachListeners();

    // If there is only one option, make the button unclickable and hide the
    // arrow svg.
    if (this.sizeBtns.length <= 1) {
      this.selector.classList.add("MattressSizeSelect__Selector--disabled");
      this.selector.setAttribute("disabled", "true");
    }
  }

  cacheElements() {
    this.openCloseHandler = this.openCloseHandler.bind(this);
    this.syncToCurrentVariant = this.syncToCurrentVariant.bind(this);

    this.selector = this.querySelector(".MattressSizeSelect__Selector");
    this.menu = this.querySelector(".MattressSizeSelect__SizeMenu");
    this.sizeBtns = this.querySelectorAll(".MattressSizeSelect__SizeBtn");
  }

  /**
   * Sync to the current selected variant in NolahPDPState.
   */
  syncToCurrentVariant() {
    console.debug(
      "product-size-select: syncToCurrentVariant",
      this.isConnected,
      this.instanceIndex
    );
    const currentSize = window.NolahPDPState.getOptionByName(
      this.getAttribute("data-option-name")
    );

    // Escape quotations for querySelector strings with //"
    const escapedValue = currentSize.replace('"', '\\"');

    const selectedBtn = this.querySelector(
      `.MattressSizeSelect__SizeBtn[data-value="${escapedValue}"]`
    );
    this.updateSelfPassively(selectedBtn);
  }

  attachListeners() {
    // Add open close handlers when clicking
    document.addEventListener("click", this.openCloseHandler);

    // Sync after change in the other
    document.addEventListener(
      "state:option-changed",
      this.syncToCurrentVariant
    );
  }

  detachListeners() {
    document.removeEventListener("click", this.openCloseHandler);
    document.removeEventListener(
      "state:option-changed",
      this.syncToCurrentVariant
    );
  }

  openCloseHandler(e) {
    if (e.target === this.selector) {
      this.openMenu();
    } else if (Array.from(this.sizeBtns).includes(e.target)) {
      this.select(e.target);
    } else {
      this.closeMenu();
    }
  }

  /**
   * Include path and position with console error. This function was added to
   * help find a non-reproducible bug where multiple instances of the size
   * select are created.
   */
  logErrorWithContext(message) {
    const path = window.DUtils.getPathTo(this);
    const position = window.DUtils.getPageXY(this);
    console.error(message, {
      iCount: window.ProductSizeSelectDebugObj.iCount,
      isConnected: this.isConnected,
      path,
      position,
    });
  }

  /**
   * Selecting a size button means choosing a new size but also resetting any
   * addons because they are specific to sizes.
   * @param button The size button that was selected
   */
  select(button) {
    const index = Array.from(this.sizeBtns).indexOf(button);
    const newPosition = index * -100;
    this.menu.style.top = newPosition + "%";

    // Set new selected button
    this.sizeBtns.forEach((b) =>
      b.classList.remove("MattressSizeSelect__SizeBtn--selected")
    );
    button.classList.add("MattressSizeSelect__SizeBtn--selected");

    // Clear any addons (since a new size was selected)
    window.NolahPDPState.clearAddons();

    // Update state and selector
    this.selector.innerHTML = button.innerHTML;

    const newSize = button.dataset.value;
    window.NolahPDPState.setOptionByName(
      this.getAttribute("data-option-name"),
      newSize
    );

    this.closeMenu();
  }

  /**
   * Does not update state, and as a result, also does not trigger
   * the state:option-changed event. Meant to be used to update its select
   * passively in response to the variant changing due to other components.
   */
  updateSelfPassively(button) {
    const index = Array.from(this.sizeBtns).indexOf(button);
    const newPosition = index * -100;
    this.menu.style.top = newPosition + "%";

    // Set new selected button
    this.sizeBtns.forEach((b) =>
      b.classList.remove("MattressSizeSelect__SizeBtn--selected")
    );
    try {
      button.classList.add("MattressSizeSelect__SizeBtn--selected");

      // Update selector
      this.selector.innerHTML = button.innerHTML;
      const coverLengthElement = document.querySelector(".cover-option-length");
    if (coverLengthElement) {
      const coverLength = parseInt(coverLengthElement.textContent.trim(), 10);
      if (coverLength > 0) {
        updateVariantPrices();
      }
    }
    setTimeout(getCartTotalAndUpdateDisplay, 100);
    } catch (err) {
      this.logErrorWithContext(err);
      window.PDPPageErrorMsg.display();
    }
  }

  openMenu() {
    this.menu.classList.remove("MattressSizeSelect__SizeMenu--hidden");
  }

  closeMenu() {
    this.menu.classList.add("MattressSizeSelect__SizeMenu--hidden");
  }
}
customElements.define("product-size-select", ProductSizeSelect);

/**
 * Not just a qty selector but THE qty selector for the main product on the page
 * in the product card. When we have use for more qty selectors like this, this
 * can be generalized and extend a more generic component.
 */
class MainProdQTYSelector extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    if (this.isConnected && !this.initialized) {
      this.initialized = true;
      this.cacheElements();
      this.attachListeners();
    }
  }

  disconnectedCallback() {
    if (!this.isConnected && this.initialized) {
      this.initialized = false;
      this.detachListeners();
    }
  }

  cacheElements() {
    this.minusBtn = this.querySelector(".ProdQTYSelector__Minus");
    this.plusBtn = this.querySelector(".ProdQTYSelector__Plus");
    this.input = this.querySelector(".ProdQTYSelector__Input");

    this.incrementQty = this.incrementQty.bind(this);
    this.decrementQty = this.decrementQty.bind(this);
    this.handleChangeQty = this.handleChangeQty.bind(this);
  }

  attachListeners() {
    this.minusBtn.addEventListener("click", this.decrementQty);
    this.plusBtn.addEventListener("click", this.incrementQty);
    this.input.addEventListener("change", this.handleChangeQty);
  }

  decrementQty() {
    const current = parseInt(this.input.value);
    const newQty = Math.max(current - 1, 1);
    this.input.value = newQty;
    this.handleChangeQty();
  }

  incrementQty() {
    const current = parseInt(this.input.value);
    this.input.value = current + 1;
    this.handleChangeQty();
  }

  handleChangeQty() {
    window.NolahPDPState.setQtyToAdd(parseInt(this.input.value));
  }

  detachListeners() {
    this.minusBtn.removeEventListener("click", this.decrementQty);
    this.plusBtn.removeEventListener("click", this.incrementQty);
    this.input.removeEventListener("click", this.handleChangeQty);
  }
}
customElements.define("main-prod-qty-selector", MainProdQTYSelector);

/**
 * The wrapping component around the firmness buttons.
 */
class MattressFirmnessArea extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    if (this.isConnected) {
      this.cacheElements();
    }

    if (this.isConnected && !this.initialized) {
      this.initialized = true;

      this.showViewBasedOnWidth();
      this.mql.addListener(this.update);

      this.classList.add("MattressFirmnessArea--loaded");
    }
  }

  disconnectedCallback() {
    if (!this.isConnected && this.initialized) {
      this.initialized = false;
      this.mql.removeListener(this.update);
    }
  }

  cacheElements() {
    this.update = this.update.bind(this);

    // Media query list listener added when connected to DOM
    this.mql = window.matchMedia("(max-width: 768px)");
    this.isShowingMobileView = this.mql.matches;

    // This block is moved to a different location on mobile to match the designs
    this.mobileAnchorEl = document.querySelector(
      '[data-mobile-anchor="mattress-firmness-area"]'
    );
    this.desktopAnchorEl = document.querySelector("#size-option-list-block");
  }

  update(e) {
    this.isShowingMobileView = e.matches;
    this.showViewBasedOnWidth();
  }

  showViewBasedOnWidth() {
    if (this.isShowingMobileView) {
      this.showMobileView();
    } else {
      this.showDesktopView();
    }
  }

  showMobileView() {
    // Insert after the mobile anchor
    if (this.mobileAnchorEl) {
      this.mobileAnchorEl.parentNode.insertBefore(
        this,
        this.mobileAnchorEl.nextElementSibling
      );
    }
  }

  showDesktopView() {
    // Insert after the desktop anchor
    if (this.desktopAnchorEl) {
      this.desktopAnchorEl.parentNode.insertBefore(
        this,
        this.desktopAnchorEl.nextElementSibling
      );
    }
  }
}
customElements.define("mattress-firmness-area", MattressFirmnessArea);

/**
 * These set of buttons offer a way to select firmness options.
 * If the split king option is selected, then two rows of these firmnesses
 * appear, and the user can choose two firmnesses, one per side of the split king
 * mattress.
 */
class MattressFirmnessBtns extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    if (this.isConnected && !this.initialized) {
      this.initialized = true;

      this.cacheElements();
      this.initState();

      this.syncToCurrentVariant();
      this.attachListeners();

      // Remove loading css class
      this.classList.remove("FirmnessOptions--loading");
    }
  }

  disconnectedCallback() {
    if (!this.isConnected && this.initialized) {
      this.initialized = false;
      this.detachListeners();
    }
  }

  cacheElements() {
    // Collect all possible firmness values so we can render buttons for them.
    if (window.NolahPDPState.hasFirmnessOption()) {
      const firmnessKey = window.NolahPDPState.getOptionKey("Firmness");
      this.firmnessValues = [];
      window.NolahProductJSON.product.variants.forEach((v) => {
        const firmness = v[firmnessKey];
        if (!this.firmnessValues.includes(firmness)) {
          this.firmnessValues.push(firmness);
        }
      });
    }
  }

  initState() {
    const [firmnessOne, firmnessTwo] =
      window.NolahPDPState.getSplitFirmnesses();
    this.state = {
      selectedFirmnesses: {
        side1: firmnessOne,
        side2: firmnessTwo,
      },
    };
  }

  /**
   * Updates state and renders area to match the current variant.
   */
  syncToCurrentVariant() {
    const [firmnessOne, firmnessTwo] =
      window.NolahPDPState.getSplitFirmnesses();

    // Update this.state if the current firmness is different from the one in this.state.
    const firmnessChanged =
      this.state.selectedFirmnesses.side1 !== firmnessOne ||
      this.state.selectedFirmnesses.side2 !== firmnessTwo;
    if (firmnessChanged) {
      this.state = {
        selectedFirmnesses: {
          side1: firmnessOne,
          side2: firmnessTwo,
        },
      };
    }

    this.render();
  }

  render() {
    let html;
    const selectedSize = window.NolahPDPState.getOptionByName("Size");
    if (window.NolahPDPState.isMattressProd && selectedSize == "Split King") {
      html = `
        <p class="FirmnessOptions__SplitKingInfo">${
          window.languages.splitKingFirmnessBtnDescription
        }</p>

        <h5 class="FirmnessOptions__SideHeading">Mattress One</h5>
        <div class="FirmnessOptions__List">
          ${this.getRowOfBtnsHTML(1, selectedSize)}
        </div>
        <h5 class="FirmnessOptions__SideHeading">Mattress Two</h5>
        <div class="FirmnessOptions__List">
          ${this.getRowOfBtnsHTML(2, selectedSize)}
        </div>
      `;
    } else {
      html = `
        <div class="FirmnessOptions__List">
          ${this.getRowOfBtnsHTML(1, selectedSize)}
        </div>
      `;
    }

    this.innerHTML = html;
  }

  /**
   * Get the html for a row with Plush, Luxury Firm and Firm buttons.
   * @param sideNumber An id to help selectBtnListener know which buttons are checked.
   * @param selectedSize Optional helper info to help style some of the buttons.
   */
  getRowOfBtnsHTML(sideNumber = 1, selectedSize) {
    const { selectedFirmnesses } = this.state;
    return this.firmnessValues
      .map(
        (value) => `
        <div class="FirmnessOptions__Item">
          <input
            class="FirmnessOptions__RadioInput"
            type="radio"
            name="firmness-option-side-${sideNumber}"
            value="${value}"
            id="firmness-option-side-${sideNumber}-${value}"
            data-side="${sideNumber}"
            ${
              value === selectedFirmnesses["side" + sideNumber] ? "checked" : ""
            }
          >
          <label
            class="FirmnessOptions__RadioLabel${
              selectedSize === "Split King"
                ? " FirmnessOptions__RadioLabel--skinny"
                : ""
            }"
            for="firmness-option-side-${sideNumber}-${value}"
          >
            ${value}
          </label>
        </div>
    `
      )
      .join("");
  }

  attachListeners() {
    this.selectBtnListener = this.selectBtnListener.bind(this);
    this.syncToCurrentVariant = this.syncToCurrentVariant.bind(this);

    this.addEventListener("change", this.selectBtnListener);
    document.addEventListener(
      "state:option-changed",
      this.syncToCurrentVariant
    );
  }

  /**
   * Selects a firmness option value by looking at the checked buttons.
   */
  selectBtnListener(e) {
    if (e.target.matches(".FirmnessOptions__RadioInput")) {
      // Clear any addons (since a new firmness was selected)
      window.NolahPDPState.clearAddons();

      const sideNumber = e.target.dataset.side;

      // See which inputs are checked
      const side1Input = this.querySelector(
        '.FirmnessOptions__RadioInput[data-side="1"]:checked'
      );
      const side2Input = this.querySelector(
        '.FirmnessOptions__RadioInput[data-side="2"]:checked'
      );
      const newFirmnesses = [side1Input.value];

      // If there is a second firmness value, then we are setting a split king
      // style firmness value with two firmnesses.
      if (side2Input) {
        newFirmnesses.push(side2Input.value);

        // Remember which were selected for reference when rendering
        this.state.selectedFirmnesses = {
          side1: newFirmnesses[0],
          side2: newFirmnesses[1],
        };

        // Update the state
        window.NolahPDPState.setSplitFirmnesses(newFirmnesses);

        // Otherwise we are simply setting a single firmness value.
      } else {
        window.NolahPDPState.setOptionByName("Firmness", side1Input.value);
        this.state.selectedFirmnesses = {
          side1: side1Input.value,
          side2: null,
        };
      }
    }
  }

  detachListeners() {
    this.removeEventListener("change", this.selectBtnListener);
    document.removeEventListener(
      "state:option-changed",
      this.syncToCurrentVariant
    );
  }
}
customElements.define("mattress-firmness-btns", MattressFirmnessBtns);

/**
 * The top price labels under the main title
 */
class MPFTitlePriceTag extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    if (this.isConnected) {
      this.cacheElements();
    }

    if (this.isConnected && !this.initialized) {
      this.initialized = true;
      this.attachListeners();
    }
  }

  disconnectedCallback() {
    if (!this.isConnected && this.initialized) {
      this.initialized = false;
      this.detachListeners();
    }
  }

  cacheElements() {
    this.update = this.update.bind(this);
    this.compareAtLabel = this.querySelector(".MPFTitleBlock__CompareAt");
    this.priceLabel = this.querySelector(".MPFTitleBlock__Price");
  }

  attachListeners() {
    document.addEventListener("state:option-changed", this.update);
  }

  detachListeners() {
    document.removeEventListener("state:option-changed", this.update);
  }

  update() {
    const [price, comparePrice] = window.NolahPDPState.getPriceTuple();
    const priceTag = document.querySelector("mpf-title-price-tag");
    const discountPercentage = parseFloat(
      priceTag.getAttribute("discounted-value")
    );
    // Check if the 'mpf-title-price-tag' element exists
    this.priceLabel.innerHTML = NUtils.formatMoney(price);
    this.compareAtLabel.innerHTML = NUtils.formatMoney(comparePrice);
  }
}
customElements.define("mpf-title-price-tag", MPFTitlePriceTag);

/**
 * Affirm label at the top near the product title.
 */
class MPFAffirmTopLabel extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    if (this.isConnected) {
      this.cacheElements();
    }

    if (this.isConnected && !this.initialized) {
      this.initialized = true;

      // The contents of the affirm label change for mobile (becomes shorter)
      this.mql = window.matchMedia("(max-width: 768px)");
      this.mql.addListener(this.update);
      this.isShowingMobileView = this.mql.matches;
      this.showViewBasedOnWidth();

      document.addEventListener("state:option-changed", this.updatePriceAmt);
      document.addEventListener("state:addons-changed", this.updatePriceAmt);
    }
  }

  disconnectedCallback() {
    if (!this.isConnected && this.initialized) {
      this.initialized = false;

      this.mql.removeListener(this.update);
      document.removeEventListener("state:option-changed", this.updatePriceAmt);
      document.removeEventListener("state:addons-changed", this.updatePriceAmt);
    }
  }

  cacheElements() {
    this.update = this.update.bind(this);
    this.updatePriceAmt = this.updatePriceAmt.bind(this);
  }

  updatePriceAmt() {
    this.dataset.amount = window.NolahPDPState.totalPrice;
    this.showViewBasedOnWidth();
  }

  update(e) {
    this.isShowingMobileView = e.matches;
    this.showViewBasedOnWidth();
  }

  showViewBasedOnWidth() {
    if (this.isShowingMobileView) {
      this.showMobileView();
    } else {
      this.showDesktopView();
    }
  }

  showMobileView() {
    window.AffirmEstimateLabels.initElements(
      ".MPFTitleBlock__AffirmLabel",
      (dollars) => `
        <span class="affirm-ala-price">\$${dollars}</span>/mo with <span class="__affirm-logo __affirm-logo-blue __ligature__affirm_full_logo__ __processed">Affirm</span>
      `
    );
  }

  showDesktopView() {
    window.AffirmEstimateLabels.initElements(
      ".MPFTitleBlock__AffirmLabel",
      (dollars) => `
        <span class="affirm-ala-price">\$${dollars}</span>/mo with <span class="__affirm-logo __affirm-logo-blue __ligature__affirm_full_logo__ __processed">Affirm</span>
      `
    );
  }
}
customElements.define("mpf-affirm-top-label", MPFAffirmTopLabel);

/**
 * Desktop only description tabs
 */
class ProductDescTabs extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    if (this.isConnected) {
      this.cacheElements();
    }

    if (this.isConnected && !this.initialized) {
      this.initialized = true;
      this.initTabs();
    }
  }

  disconnectedCallback() {
    if (!this.isConnected && this.initialized) {
      this.initialized = false;
      this.detachDocListeners();
    }
  }

  cacheElements() {
    this.debouncedUpdateSelectedBar = NUtils.debounce(
      this.updateTablistSelectedBar.bind(this),
      100
    );
    this.tabs = this.querySelectorAll(".ProductDescTabs__Tab");
    this.tablist = this.querySelector(".ProductDescTabs__Header");
    this.tabPanels = this.querySelectorAll(".ProductDescTabs__TextBlock");
  }

  initTabs() {
    this.tabs.forEach((tab) => {
      tab.addEventListener("click", () => this.selectTab(tab));

      if (tab.getAttribute("aria-selected") === "true") {
        this.selectTab(tab);
      }
    });

    // Update tablist 'selected bar' on media query change
    window.addEventListener("resize", this.debouncedUpdateSelectedBar);

    // Update again on load to account for when the fonts load in
    window.addEventListener("load", this.debouncedUpdateSelectedBar);
  }

  detachDocListeners() {
    window.removeEventListener("resize", this.debouncedUpdateSelectedBar);
    window.removeEventListener("load", this.debouncedUpdateSelectedBar);
  }

  selectTab(tab) {
    const category = tab.dataset.category;
    const matchingPanel = Array.from(this.tabPanels).find(
      (p) => p.dataset.category === category
    );

    // Update aria selected
    this.tabs.forEach((t) => t.setAttribute("aria-selected", "false"));
    tab.setAttribute("aria-selected", "true");
    this.tabPanels.forEach((t) => t.setAttribute("aria-hidden", "true"));
    matchingPanel.setAttribute("aria-hidden", "false");

    this.updateTablistSelectedBar();
  }

  updateTablistSelectedBar() {
    const bar = this.tablist.querySelector(
      ".ProductDescTabs__SelectedBarInner"
    );
    const selectedTab = this.tablist.querySelector(
      '.ProductDescTabs__Tab[aria-selected="true"]'
    );
    const tablistXPos = this.tablist.getBoundingClientRect().left;
    const selectedTabRect = selectedTab.getBoundingClientRect();

    const barNewXPos = selectedTabRect.left - tablistXPos;
    const barNewWidth = selectedTabRect.width;
    bar.style.left = barNewXPos + "px";
    bar.style.width = barNewWidth + "px";
  }
}
customElements.define("product-desc-tabs", ProductDescTabs);

/**
 * Desktop only description tabs
 */
class ProductDescDrawers extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    if (this.isConnected) {
      this.cacheElements();
    }

    if (this.isConnected && !this.initialized) {
      this.initialized = true;
      this.initDrawers();
    }
  }

  cacheElements() {
    this.triggers = this.querySelectorAll(".PDescDrawers__Trigger");
    this.panels = this.querySelectorAll(".PDescDrawers__Panel");
  }

  initDrawers() {
    // Set height values to open panels so that css transitions can happen
    const openPanels = this.querySelectorAll(
      ".PDescDrawers__Panel:not(.PDescDrawers__Panel--startClosed)"
    );
    openPanels.forEach((p) => {
      p.style.height = p.scrollHeight + "px";
    });

    // Set default state by checking for the class 'PDescDrawers__Panel--startClosed'
    const closedPanels = this.querySelectorAll(
      ".PDescDrawers__Panel--startClosed"
    );
    closedPanels.forEach((p) => {
      p.style.height = "0px";
      p.classList.remove("PDescDrawers__Panel--startClosed");
    });

    this.triggers.forEach((trigger) =>
      trigger.addEventListener("click", (e) => {
        const category = trigger.dataset.category;
        const matchingPanel = Array.from(this.panels).find(
          (p) => p.dataset.category === category
        );

        NUtils.slideToggle(matchingPanel);

        NUtils.toggleBooleanAttribute(trigger, "aria-expanded");
      })
    );
  }
}
customElements.define("product-desc-drawers", ProductDescDrawers);

/**
 * This area moves to under the size select on mobile, but is in its own option
 * box on desktop.
 */
class ProductATCArea extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    if (this.isConnected && !this.initialized) {
      this.initialized = true;
      this.cacheElements();
      this.showViewBasedOnWidth();
      this.mql.addListener(this.update);
    }
  }

  disconnectedCallback() {
    if (!this.isConnected && this.initialized) {
      this.initialized = false;
      this.mql.removeListener(this.update);
    }
  }

  cacheElements() {
    this.update = this.update.bind(this);

    // Media query list listener added when connected to DOM
    this.mql = window.matchMedia("(max-width: 768px)");
    this.isShowingMobileView = this.mql.matches;

    // This block is moved to a different location on mobile to match the designs
    this.mobileAnchorEl = document.querySelector(
      '[data-mobile-anchor="product-atc-area"]'
    );
    this.desktopAnchorEl = document.querySelector(
      '[data-desktop-anchor="product-atc-area"]'
    );
  }

  update(e) {
    this.isShowingMobileView = e.matches;
    this.showViewBasedOnWidth();
  }

  showViewBasedOnWidth() {
    if (this.isShowingMobileView) {
      this.showMobileView();
    } else {
      this.showDesktopView();
    }
  }

  showMobileView() {
    // Insert after the mobile anchor
    if (this.mobileAnchorEl) {
      this.mobileAnchorEl.parentNode.insertBefore(
        this,
        this.mobileAnchorEl.nextElementSibling
      );
    }
  }

  showDesktopView() {
    // Insert after the desktop anchor
    if (this.desktopAnchorEl) {
      this.desktopAnchorEl.parentNode.insertBefore(
        this,
        this.desktopAnchorEl.nextElementSibling
      );
    }
  }
}
customElements.define("product-atc-area", ProductATCArea);

/**
 * A price component that shows the price today on the left, and the affirm
 * price on the right.
 */
class FinalPricesBlock extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    if (this.isConnected) {
      this.cacheElements();
    }

    if (this.isConnected && !this.initialized) {
      this.initialized = true;
      this.syncToCurrentVariant();
      this.attachListeners();
    }
  }

  disconnectedCallback() {
    if (!this.isConnected && this.initialized) {
      this.initialized = false;
      this.detachListeners();
    }
  }

  cacheElements() {
    this.syncToCurrentVariant = this.syncToCurrentVariant.bind(this);

    this.todayPrice = this.querySelector(".FinalPricesBlock__TodayPrice");
    this.todayCompareAt = this.querySelector(
      ".FinalPricesBlock__PayToday .StrikeLabel"
    );
    this.savingsLabel = this.querySelector(".FinalPricesBlock__SavingsLabel");
    this.monthlyPrice = this.querySelector(".FinalPricesBlock__MonthlyPrice");
    this.monthlyPriceLearnMore = this.querySelector(
      ".FinalPricesBlock__PriceFooter--monthlyInfo"
    );
  }

  syncToCurrentVariant() {
    const totalPrice = window.NolahPDPState.totalPrice;
    const totalComparePrice = window.NolahPDPState.totalComparePrice;
    const priceTag = document.querySelector("mpf-title-price-tag");
    const discountPercentage = parseFloat(
      priceTag.getAttribute("discounted-value")
    );
    // Initialize adjusted price variables with the original total and compare prices
    // These variables will be modified if any discount is applied
    let adjustedTotalPrice = totalPrice; // Holds the final price after applying discounts (initially set to the original total price)
    let adjustedComparePrice = totalComparePrice; // Holds the original price before discount for comparison (initially set to the original compare price)

    // Check if the 'mpf-title-price-tag' element exists
    if (discountPercentage) {
      const discountAmount = Math.floor(
        (totalPrice * discountPercentage) / 100
      );
      adjustedTotalPrice = totalPrice - discountAmount;
      adjustedComparePrice = totalPrice;
      this.todayPrice.innerHTML = NUtils.formatMoney(totalPrice);
      this.todayCompareAt.innerHTML = NUtils.formatMoney(totalComparePrice);
    } else {
      // this.todayPrice.innerHTML = NUtils.formatMoney(totalPrice);
      // this.todayCompareAt.innerHTML = NUtils.formatMoney(totalComparePrice);
      this.todayPrice.innerHTML = NUtils.formatMoney(totalPrice);
      this.todayCompareAt.innerHTML = NUtils.formatMoney(totalComparePrice);
    }
    const savingsAmt = totalComparePrice - totalPrice;

    if (savingsAmt > 0) {
      if (window.NolahPDPState.isMattressProd) {
        this.savingsLabel.innerHTML = `35% off with offer`;
      } else if (window.NolahPDPState.isWeightedBlanket) {
        this.savingsLabel.innerHTML = `70% off with offer`;
      } else if (window.NolahPDPState.hasSale50Tag) {
        this.savingsLabel.innerHTML = `50% off with offer`;
      } else {
        this.savingsLabel.innerHTML = `${NUtils.formatMoney(
          savingsAmt
        )} off with offer`;
      }
    } else {
      this.savingsLabel.innerHTML = "";
    }

    // Update the financing area.
    // Don't show the affirm area if the price is under $250.
    if (adjustedTotalPrice <= 25000) {
      this.classList.add("FinalPricesBlock--hideFinancingBlock");
    } else {
      this.classList.remove("FinalPricesBlock--hideFinancingBlock");
      this.monthlyPrice.dataset.amount = adjustedTotalPrice;
      this.monthlyPriceLearnMore.dataset.amount = adjustedTotalPrice;
      window.AffirmEstimateLabels.initElementsWithExistingHTML(
        ".FinalPricesBlock__MonthlyPrice"
      );
      window.AffirmEstimateLabels.initElWithoutEditingHTML(
        this.monthlyPriceLearnMore
      );
    }
  }

  /**
   * Note that there can be more than one final-prices-block element.
   */
  attachListeners() {
    document.addEventListener(
      "state:option-changed",
      this.syncToCurrentVariant
    );
    document.addEventListener(
      "state:addons-changed",
      this.syncToCurrentVariant
    );
    document.addEventListener(
      "state:qty-input-changed",
      this.syncToCurrentVariant
    );
    document.addEventListener(
      "mulberry-warranty-changed",
      this.syncToCurrentVariant
    );
  }

  detachListeners() {
    document.removeEventListener(
      "state:option-changed",
      this.syncToCurrentVariant
    );
    document.removeEventListener(
      "state:addons-changed",
      this.syncToCurrentVariant
    );
    document.removeEventListener(
      "state:qty-input-changed",
      this.syncToCurrentVariant
    );
    document.removeEventListener(
      "mulberry-warranty-changed",
      this.syncToCurrentVariant
    );
  }
}
customElements.define("final-prices-block", FinalPricesBlock);

/**
 * The atc button.
 */
class ProductATCBtn extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    if (this.isConnected) {
      this.cacheElements();
    }

    if (this.isConnected && !this.initialized) {
      this.initialized = true;

      this.attachListeners();
    }
  }

  disconnectedCallback() {
    if (!this.isConnected && this.initialized) {
      this.initialized = false;

      this.detachListeners();
    }
  }

  cacheElements() {
    this.atcClickHandler = this.atcClickHandler.bind(this);

    this.button = this.querySelector(".MattressProductATCBtn__Btn");
  }

  attachListeners() {
    this.button.addEventListener("click", this.atcClickHandler);
  }

  detachListeners() {
    this.button.removeEventListener("click", this.atcClickHandler);
  }

  atcClickHandler(e) {
    e.preventDefault();

    const items = this.getItemsToAdd();

    // Disable button
    this.button.setAttribute("disabled", "disabled");

    // Start loading bar
    document.dispatchEvent(new CustomEvent("theme:loading:start"));

    // Add to cart
    window.NolahCartManager.addItems(items)
      .catch((err) => {
        console.error("ATC Button", err);
      })
      .finally(() => {
        // Finish loading bar
        document.dispatchEvent(new CustomEvent("theme:loading:end"));
        this.button.removeAttribute("disabled");
      });
  }

  getItemsToAdd() {
    const items = [];
    const currentVariant = window.NolahPDPState.currentVariant;
    const currentQuantity = window.NolahPDPState.mainProdQtyToAdd;

    items.push({ id: currentVariant.id, quantity: currentQuantity });

    window.NolahPDPState.addonsSelected.forEach((addon) => {
      items.push({
        id: addon.id,
        quantity: 1,
      });
    });

    return items;
  }
}
customElements.define("product-atc-btn", ProductATCBtn);

/**
 * The top price labels under the main title
 */
class SizeSpecificInfoLabel extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    if (this.isConnected) {
      this.cacheElements();
    }

    if (this.isConnected && !this.initialized) {
      this.initialized = true;
      this.attachListeners();
    }
  }

  disconnectedCallback() {
    if (!this.isConnected && this.initialized) {
      this.initialized = false;
      this.detachListeners();
    }
  }

  cacheElements() {
    try {
      this.infoJSON = JSON.parse(
        this.querySelector("[data-size-specific-info-label-json]").innerHTML
      );
    } catch (err) {
      console.warn(
        "Size specific info JSON may not be formatted correctly in settings",
        err
      );
      this.remove();
      return;
    }

    this.update = this.update.bind(this);
    this.labelContents = this.querySelector(".SizeSpecificInfoLabel__Contents");
  }

  attachListeners() {
    document.addEventListener("state:option-changed", this.update);
  }

  detachListeners() {
    document.removeEventListener("state:option-changed", this.update);
  }

  update() {
    const size = window.NolahPDPState.getOptionByName("Size");
    this.labelContents.innerHTML = this.getLabel(size);
  }

  getLabel(size) {
    return this.infoJSON[size];
  }
}
customElements.define("size-specific-info-label", SizeSpecificInfoLabel);

/**
 * Miscellaneous product page code that doesn't fit in a custom element.
 */
class NolahProductPage {
  constructor() {
    // Disabled because this is how honey realizes the variant changes and
    // dramatically affects page performance.
    // this.initHistoryListener();

    this.initSizeChartBtns();
    this.initDeliveryInfoPopup();
    this.initFirmnessPopup();
    this.initSecondOptionPopup();
    this.initThirdOptionPopup();
  }

  initSizeChartBtns() {
    const btns = document.querySelectorAll("[data-open-size-chart]");
    btns.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        const contentsEl = document.querySelector(
          "#product-mattress-size-chart"
        );
        window.GlobalNPopup.openWithContents(
          "size-chart",
          contentsEl.innerHTML
        );
      });
    });
  }

  initDeliveryInfoPopup() {
    const btns = document.querySelectorAll("[data-open-delivery-info]");
    btns.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        const contentsEl = document.querySelector(
          "#product-mattress-delivery-info"
        );
        window.GlobalNPopup.openWithContents(
          "delivery-info",
          contentsEl.innerHTML
        );
      });
    });

    // For the headboard base only.
    const headboardShippingNoticeBtns = document.querySelectorAll(
      "[data-open-headboard-shipping-notice]"
    );
    headboardShippingNoticeBtns.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        const contentsEl = document.querySelector(
          "#headboard-shipping-notice-template"
        );
        window.GlobalNPopup.openWithContents(
          "headboard-shipping-info",
          contentsEl.innerHTML
        );
      });
    });
  }

  initFirmnessPopup() {
    const btn = document.querySelector("[data-open-firmness-popup]");
    if (!btn) return;

    // Open and prepare the popup on click
    btn.addEventListener("click", (e) => {
      const contentsEl = document.querySelector(
        "#product-mattress-firmness-popup"
      );
      window.GlobalNPopup.loadWithContents("firmness", contentsEl.innerHTML);
      const popupEl = window.GlobalNPopup.popup;
      const popupInputs = popupEl.querySelectorAll(".FirmnessPopup__Input");
      const formInputs = document.querySelectorAll(
        ".FirmnessOptions__RadioInput"
      );

      // Sync the checked state of the options in the popup
      // const currentFirmness = NolahPDPState.getOptionByName('Firmness');
      // if (currentFirmness) {
      //   popupInputs.forEach(input => {
      //     if (currentFirmness === input.value) {
      //       input.checked = true;
      //     }
      //   });
      // }

      // Bind the inside buttons to the form's firmness buttons
      // popupInputs.forEach(popupRadioInput => {
      //   popupRadioInput.addEventListener('change', e => {
      //     const matchingFormInput = Array.from(formInputs).find(
      //       i => (i.value === popupRadioInput.value)
      //     );
      //     if (matchingFormInput) {
      //       matchingFormInput.click();
      //     }
      //   });
      // });

      window.GlobalNPopup.open();
    });
  }

  initSecondOptionPopup() {
    const btn = document.querySelector(`[data-open-learn-more="2"]`);
    if (!btn) return;

    // Open and prepare the popup on click
    btn.addEventListener("click", (e) => {
      const contentsEl = document.querySelector("#product-option-second-popup");
      window.GlobalNPopup.loadWithContents(
        "second-option",
        contentsEl.innerHTML
      );
      window.GlobalNPopup.open();
    });
  }

  initThirdOptionPopup() {
    const btn = document.querySelector(`[data-open-learn-more="3"]`);
    if (!btn) return;

    // Open and prepare the popup on click
    btn.addEventListener("click", (e) => {
      const contentsEl = document.querySelector("#product-option-third-popup");
      window.GlobalNPopup.loadWithContents(
        "third-option",
        contentsEl.innerHTML
      );
      window.GlobalNPopup.open();
    });
  }

  /**
   * Update url with every variant change
   */
  initHistoryListener() {
    document.addEventListener("state:option-changed", (e) => {
      console.debug("state:option-changed, pushing new state");
      const url = new URL(window.location);
      url.searchParams.set("variant", window.NolahPDPState.currentVariant.id);
      window.history.pushState({}, "", url);
    });
  }
}

new NolahProductPage();



class ProductCoverSelect extends HTMLElement {
  constructor() {
    super();
    this.selectCoverHandler = this.selectCoverHandler.bind(this);
    this.toggleDropdown = this.toggleDropdown.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
  }

  connectedCallback() {
    this.coverButtons = this.querySelectorAll(".MattressSizeSelect__SizeBtn");
    this.label = this.querySelector(".MattressSizeSelect__Size");
    this.dropdown = this.querySelector(".MattressSizeSelect__SizeMenu");
    this.selectorButton = this.querySelector(".MattressSizeSelect__Selector");
    this.hiddenSelect = this.querySelector("select");

    this.selectorButton?.addEventListener("click", this.toggleDropdown);
    document.addEventListener("click", this.handleClickOutside);

    this.coverButtons?.forEach((btn) => {
      btn.addEventListener("click", this.selectCoverHandler);
    });
  }

  disconnectedCallback() {
    this.selectorButton?.removeEventListener("click", this.toggleDropdown);
    document.removeEventListener("click", this.handleClickOutside);

    this.coverButtons?.forEach((btn) => {
      btn.removeEventListener("click", this.selectCoverHandler);
    });
  }

  toggleDropdown(event) {
    event.stopPropagation();
    this.dropdown?.classList.toggle("MattressSizeSelect__SizeMenu--hidden");
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      this.dropdown?.classList.add("MattressSizeSelect__SizeMenu--hidden");
    }
  }

  selectCoverHandler(e) {
    const btn = e.currentTarget;
    const selectedValue = btn.dataset.value;
    const selectedLabel = btn.dataset.label;
    this.coverButtons.forEach((button) => {
      button.classList.remove("is-selected");
    });
    btn.classList.add("is-selected");
    if (this.label) this.label.textContent = selectedLabel;
    if (this.hiddenSelect) {
      this.hiddenSelect.value = selectedValue;
      this.hiddenSelect.dispatchEvent(new Event("change", { bubbles: true }));
    }
    if (window.NolahPDPState) {
      window.NolahPDPState.setOptionByName("Cover", selectedValue);
    }
    this.dropdown?.classList.add("MattressSizeSelect__SizeMenu--hidden");
  }
}

customElements.define("product-cover-select", ProductCoverSelect);


function updateVariantPrices() {
  const firstPriceEl = document.querySelector('[data-variant-number="1"] b');
  const secondPriceEl = document.querySelector('[data-variant-number="2"] b');
  const finalPriceEl = document.querySelector('.FinalPricesBlock__TodayPrice');
  if (firstPriceEl) {
    document.querySelector('.first-variant').textContent = firstPriceEl.textContent.trim();
  }
  if (secondPriceEl) {
    document.querySelector('.second-variant').textContent = secondPriceEl.textContent.trim();
  }
  if (finalPriceEl) {
    document.querySelector('.final-price').textContent = finalPriceEl.textContent.trim();
  }
}
/**
 * Function to filter and display upsell variant items based on currently selected size
 */
function filterUpsellVariantsBySize() {
  if (!window.NolahPDPState) {
    console.warn('NolahPDPState not available');
    return;
  }

  const currentSize = window.NolahPDPState.getOptionByName('Size');
  if (!currentSize) {
    console.warn('No size selected');
    return;
  }
  const currentSizeClean = currentSize.split('/')[0].trim();
  const variantItems = document.querySelectorAll('.upsell-variant-item');
  
  if (variantItems.length === 0) {
    return;
  }
  variantItems.forEach(item => {
    const itemVariantTitle = item.getAttribute('data-variant-title');
    
    if (itemVariantTitle === currentSizeClean) {
      item.style.display = 'flex';
    } else {
      item.style.display = 'none';
    }
  });

  console.debug('Filtered upsell variants for size:', currentSizeClean);
}
window.addEventListener('DOMContentLoaded', () => {
  updateVariantPrices();
  filterUpsellVariantsBySize();
});
document.addEventListener('state:option-changed', (e) => {
  // Only filter when size option changes
  if (e.detail && e.detail.name === 'Size') {
    filterUpsellVariantsBySize();
  }
});
window.filterUpsellVariantsBySize = filterUpsellVariantsBySize;

// WGD element functionality code starts here
async function getCartTotalAndUpdateDisplay() {
  try {
    const response = await fetch('/cart.js');
    const cart = await response.json();
    const cartTotal = cart.total_price / 100;

    document.querySelectorAll('.wgd-cart-total').forEach(el => {
      el.textContent = cartTotal;
    });
    const productPriceEl = document.querySelector('.FinalPricesBlock__TodayPrice');
    let productPrice = 0;

    if (productPriceEl) {
      const rawText = productPriceEl.textContent.trim();
      const cleanedPrice = rawText.replace(/[$,]/g, '');
      productPrice = parseFloat(cleanedPrice) || 0;
    }
    const combinedTotal = cartTotal + productPrice;
    document.querySelectorAll('.total').forEach(el => {
      el.textContent = combinedTotal;
    });
    let threshold = 2000;
    const thresholdEl = document.querySelector('.wgd-eligible-amount');
    if (thresholdEl) {
      const thresholdText = thresholdEl.textContent.trim();
      threshold = parseFloat(thresholdText) || threshold;
    }
    const container = document.querySelector('.wgd-pdp-element-container');
    if (container) {
      if (combinedTotal >= threshold) {
        container.classList.add('eligible-wgd');
      } else {
        container.classList.remove('eligible-wgd');
      }
    }

    return combinedTotal;

  } catch (error) {
    console.error('Error fetching or calculating total:', error);
  }
}
document.addEventListener('DOMContentLoaded', getCartTotalAndUpdateDisplay);
// WGD element functionality code ends here